import React from "react";
import { Route } from "react-router-dom";
import { LazyLoadRoutes } from "routes/lazyLoadRoutes";

const MyAccount = React.lazy(() =>
  import("features/Account/modules/Settings/Settings").then((module) => ({
    default: module.Settings,
  }))
);
const ProfileDetails = React.lazy(() =>
  import("features/Account/modules/ProfileDetails/ProfileDetails").then(
    (module) => ({
      default: module.ProfileDetails,
    })
  )
);

const Features = React.lazy(() =>
  import("features/Account/modules/Features/Features").then((module) => ({
    default: module.Features,
  }))
);

const MySubscriptions = React.lazy(() =>
  import("features/Account/modules/MySubscription/MySubscription").then(
    (module) => ({
      default: module.MySubscription,
    })
  )
);

const SubscriptionTransactions = React.lazy(() =>
  import(
    "features/Account/modules/MySubscription/modules/SubscriptionTransactions/SubscriptionTransactions"
  ).then((module) => ({
    default: module.SubscriptionTransactions,
  }))
);

const Cart = React.lazy(() =>
  import(
    "features/Account/modules/MySubscription/modules/Cart/CartWrapper.jsx"
  ).then((module) => ({
    default: module.CartWrapper,
  }))
);

const PlanCart = React.lazy(() =>
  import(
    "features/Account/modules/MySubscription/modules/PlanCart/PlanCartWrapper.jsx"
  ).then((module) => ({
    default: module.PlanCartWrapper,
  }))
);

const WalletAllTransaction = React.lazy(() =>
  import("features/Account/modules/Wallets/MyWallet").then((module) => ({
    default: module.MyWallet,
  }))
);

const WalletSettlement = React.lazy(() =>
  import(
    "features/Account/modules/Wallets/modules/SettlementTable/SettlementTable"
  ).then((module) => ({
    default: module.SettlementTable,
  }))
);
const WalletSettings = React.lazy(() =>
  import(
    "features/Account/modules/Wallets/modules/WalletSettings/WalletSettings"
  ).then((module) => ({
    default: module.WalletSettings,
  }))
);

const PayoutDetailsWrapper = React.lazy(() =>
  import("features/Account/modules/PayoutDetails/PayoutDetailsWrapper").then(
    (module) => ({
      default: module.PayoutDetailsWrapper,
    })
  )
);

const CustomInvoice = React.lazy(() =>
  import(
    "features/Account/modules/PayoutDetails/CustomInvoice/CustomInvoiceWrapper"
  ).then((module) => ({
    default: module.CustomInvoiceWrapper,
  }))
);

const PaymentOptions = React.lazy(() =>
  import("features/Account/modules/PaymentOptions/PaymentOptions").then(
    (module) => ({
      default: module.PaymentOptions,
    })
  )
);

const Communications = React.lazy(() =>
  import("features/Account/modules/Communications/Communications").then(
    (module) => ({
      default: module.Communications,
    })
  )
);

const ContactForm = React.lazy(() =>
  import(
    "features/Account/modules/Communications/modules/ContactForm/ContactForm"
  ).then((module) => ({
    default: module.ContactForm,
  }))
);

const AccountLevelBookingQuestions = React.lazy(() =>
  import(
    "features/Account/modules/AccountLevelBookingQuestions/AccountLevelBookingQuestions"
  ).then((module) => ({
    default: module.AccountLevelBookingQuestions,
  }))
);

export const ACCOUNT_ROUTE_KEYS = {
  MY_ACCOUNT: "MY_ACCOUNT",
  PROFILE_DETAILS: "PROFILE_DETAILS",
  FEATURES: "FEATURES",
  MY_SUBSCRIPTION: "MY_SUBSCRIPTION",
  SUBSCRIPTION_TRANSACTIONS: "SUBSCRIPTION_TRANSACTIONS",
  CART: "CART",
  PLAN_CART: "PLAN_CART",
  MY_WALLET: "MY_WALLET",
  WALLET_ALL_TRANSACTION: "WALLET_ALL_TRANSACTION",
  WALLET_SETTLEMENT: "WALLET_SETTLEMENT",
  WALLET_SETTINGS: "WALLET_SETTINGS",
  PAYOUT_DETAILS: "PAYOUT_DETAILS",
  CUSTOM_INVOICE: "CUSTOM_INVOICE",
  PAYMENT_OPTIONS: "PAYMENT_OPTIONS",
  COMMUNICATIONS: "COMMUNICATIONS",
  EDIT_CONTACT_FORM: "EDIT_CONTACT_FORM",
  BOOKING_QUESTIONS: "BOOKING_QUESTIONS",
};

export const ACCOUNT_ROUTE_IDS = {
  [ACCOUNT_ROUTE_KEYS.MY_ACCOUNT]: "/myAccount",
  [ACCOUNT_ROUTE_KEYS.PROFILE_DETAILS]: "/profile-details",
  [ACCOUNT_ROUTE_KEYS.FEATURES]: "/features",
  [ACCOUNT_ROUTE_KEYS.MY_SUBSCRIPTION]: "/my-subscriptions",
  [ACCOUNT_ROUTE_KEYS.SUBSCRIPTION_TRANSACTIONS]: "/subscription-transactions",
  [ACCOUNT_ROUTE_KEYS.CART]: "/cart",
  [ACCOUNT_ROUTE_KEYS.PLAN_CART]: "/plan-cart/:planUuid/:ctaAction",
  [ACCOUNT_ROUTE_KEYS.WALLET_ALL_TRANSACTION]: "/my-wallet",
  [ACCOUNT_ROUTE_KEYS.WALLET_SETTLEMENT]: "/my-wallet/settlement",
  [ACCOUNT_ROUTE_KEYS.WALLET_SETTINGS]: "/my-wallet/settings",
  [ACCOUNT_ROUTE_KEYS.PAYOUT_DETAILS]: "/PayoutDetails",
  [ACCOUNT_ROUTE_KEYS.CUSTOM_INVOICE]: "/custom-invoice",
  [ACCOUNT_ROUTE_KEYS.PAYMENT_OPTIONS]: "/payments",
  [ACCOUNT_ROUTE_KEYS.COMMUNICATIONS]: "/communications",
  [ACCOUNT_ROUTE_KEYS.EDIT_CONTACT_FORM]: "/edit-contact-form",
  [ACCOUNT_ROUTE_KEYS.BOOKING_QUESTIONS]: "/booking-questions-account",
};

export const ACCOUNT_CUSTOM_ROUTES = [
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.MY_ACCOUNT]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.MY_ACCOUNT]}
    render={(props) => <LazyLoadRoutes LazyElement={MyAccount} props={props} />}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.PROFILE_DETAILS]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.PROFILE_DETAILS]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={ProfileDetails} props={props} />
    )}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.FEATURES]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.FEATURES]}
    render={(props) => <LazyLoadRoutes LazyElement={Features} props={props} />}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.MY_SUBSCRIPTION]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.MY_SUBSCRIPTION]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={MySubscriptions} props={props} />
    )}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.SUBSCRIPTION_TRANSACTIONS]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.SUBSCRIPTION_TRANSACTIONS]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={SubscriptionTransactions} props={props} />
    )}
  />,
  <Route
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.CART]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.CART]}
    render={(props) => <LazyLoadRoutes LazyElement={Cart} props={props} />}
  />,
  <Route
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.PLAN_CART]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.PLAN_CART]}
    render={(props) => <LazyLoadRoutes LazyElement={PlanCart} props={props} />}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.WALLET_ALL_TRANSACTION]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.WALLET_ALL_TRANSACTION]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={WalletAllTransaction} props={props} />
    )}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.WALLET_SETTLEMENT]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.WALLET_SETTLEMENT]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={WalletSettlement} props={props} />
    )}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.WALLET_SETTINGS]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.WALLET_SETTINGS]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={WalletSettings} props={props} />
    )}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.PAYOUT_DETAILS]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.PAYOUT_DETAILS]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={PayoutDetailsWrapper} props={props} />
    )}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.CUSTOM_INVOICE]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.CUSTOM_INVOICE]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={CustomInvoice} props={props} />
    )}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.PAYMENT_OPTIONS]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.PAYMENT_OPTIONS]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={PaymentOptions} props={props} />
    )}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.COMMUNICATIONS]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.COMMUNICATIONS]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={Communications} props={props} />
    )}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.EDIT_CONTACT_FORM]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.EDIT_CONTACT_FORM]}
    render={(props) => (
      <LazyLoadRoutes LazyElement={ContactForm} props={props} />
    )}
  />,
  <Route
    exact
    key={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.BOOKING_QUESTIONS]}
    path={ACCOUNT_ROUTE_IDS[ACCOUNT_ROUTE_KEYS.BOOKING_QUESTIONS]}
    render={(props) => (
      <LazyLoadRoutes
        LazyElement={AccountLevelBookingQuestions}
        props={props}
      />
    )}
  />,
];
